import * as React from 'react';

import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { CURRENCIES } from 'src/features/payStations/types';
import { VersionForm } from 'src/features/reservations/components/modals/VersionForm';
import {
    useCreateReservationType,
    createReservationTypeFormSchema,
    type CreateReservationTypeFormSchemaValues,
} from 'src/features/reservations/hooks/useCreateReservationType';
import { RESERVATION_TYPE, type ReservationType } from 'src/features/reservations/types/reservationType';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { type DataTableNewEntryProps } from 'src/registry/components/table';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { cn } from 'src/utils';
import { generalErrorMessages, generalMessages } from 'src/utils/messages';

interface AddNewVersionModalProps extends DataTableNewEntryProps<ReservationType> {}

function AddNewVersionModal({ onSubmit: onSubmitCallback }: AddNewVersionModalProps) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const { reservationType } = useParams();
    const { parkingId } = useParking();
    const { toast } = useToast();

    const { mutate, isPending } = useCreateReservationType({
        onSuccess: () => {
            onOpenChange(false);
            toast({
                title: formatMessage(reservationMessages.versionCreateSuccess),
                description: formatMessage(reservationMessages.versionCreateSuccessDetails),
            });
            if (onSubmitCallback) {
                onSubmitCallback();
            }
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const form = useForm<CreateReservationTypeFormSchemaValues>({
        resolver: zodResolver(createReservationTypeFormSchema),
        mode: 'onChange',
        defaultValues: {
            reserve_space: false,
            reservation_type: reservationType as RESERVATION_TYPE,
            exit_tolerance_minutes: 0,
            entry_tolerance_minutes: 0,
            allowed_spaces: 0,
            tax_percent: '0.2000',
            price: 0,
            price_with_tax: 0,
            tax_amount: 0,
            currency: CURRENCIES.EUR,
            points: [],
            paid: true,
        },
    });

    const onSubmit = (values: CreateReservationTypeFormSchemaValues) => {
        const { tax_amount, price, ...submitValues } = values;

        const submitData = {
            parkingId: parkingId ?? '',
            ...submitValues,
        };
        mutate(submitData);
        form.reset();
    };

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) {
            form.reset();
        }
        setIsModalOpen(isOpen);
    };

    return (
        <Dialog open={isModalOpen} onOpenChange={onOpenChange} modal>
            <DialogTrigger asChild>
                <Button variant="default" className={cn('mb-4 ml-auto')}>
                    <FontAwesomeIcon className="text-white" icon={faPlus} />
                    {formatMessage(reservationMessages.addNewVersion)}
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={faPlus} />
                        <div>
                            <DialogTitle>{formatMessage(reservationMessages.addNewVersion)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(reservationMessages.newVersionDescription)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <VersionForm form={form} onSubmit={form.handleSubmit(onSubmit)} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" className="flex-1" onClick={() => onOpenChange}>
                            {formatMessage(generalMessages.cancel)}
                        </Button>
                    </DialogClose>
                    <Button
                        disabled={!form.formState.isValid}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending ? (
                            <FontAwesomeIcon icon={faSpinnerThird} className="mr-2 size-4 animate-spin" />
                        ) : null}
                        {formatMessage(reservationMessages.newVersionDescription)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default AddNewVersionModal;
